import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { IssueAssessmentStatus } from '@risksmart-app/domain/src/types/consts/issue-assessment-status';
import type { UpdateIssueAssessmentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';
import { unwrapApprovalGated } from '../unwrapApprovalGated';

export type UpdateIssueAssessmentTRPCInput = {
  Id: string;
  ParentIssueId: string;
  Severity?: number | null;
  Status?: IssueAssessmentStatus | null;
  CertifiedIndividual?: string | null;
  IssueType?: string | null;
  ActualCloseDate?: string | null;
  TargetCloseDate?: string | null;
  PolicyOwnerCommentary?: string | null;
  PolicyOwner?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  PolicyBreach?: boolean | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Reportable?: boolean | null;
  PoliciesBreached?: string | null;
  Rationale?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedByThirdParty?: boolean | null;
  SystemResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  RegulatoryBreach?: boolean | null;
  RegulationsBreached?: string | null;
  ThirdPartyResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedBySystemIssue?: boolean | null;
  CustomAttributeData?: CustomAttributeData | null;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
  RegulationsBreachedIds: string[];
  AssociatedControlIds: string[];
  PoliciesBreachedIds: string[];
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): UpdateIssueAssessmentMutation {
  return {
    updateChildIssueAssessment: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
}

export const useUpdateIssueAssessmentTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const changeRequestConfirm = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.issueAssessment.update.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issue.issueAssessmentByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issue.issueById.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `control` and `tag_type`.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.control.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.tag.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateIssueAssessment: async (
      variables: UpdateIssueAssessmentTRPCInput
    ): Promise<UpdateIssueAssessmentMutation> => {
      const result = await withChangeRequestConfirm({
        input: variables,
        run: (v) => mutation.mutateAsync(v),
        confirm: changeRequestConfirm,
      });

      // A confirmed update resolves as `applied` (no chain fired) OR
      // `change-request-created` / `change-request-amended` (a CR was raised).
      // All three are success from the caller's perspective — mirror
      // useDeleteRiskTRPC and do NOT call expectApplied, which throws on the CR
      // branch and surfaces a false "Failed to save changes" toast. The entity
      // is unchanged on the CR branch, so echo the input Id.
      return mapTrpcResponseToGraphQL(
        unwrapApprovalGated(result, { Id: variables.Id })
      );
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
